/**
 * Canonical permission catalogue — mirrors the backend `EUserPermissionType`
 * enum (`apps/core-service/.../user-permission-type.enum.ts`). Permission
 * names are `resource.action`. Use these constants instead of magic strings so
 * a typo is a compile error, not a silently-ungated button.
 */
export const PERMISSIONS = {
  // SRS domain modules (six actions each)
  COMPLIANCE_VIEW: 'compliance.view',
  COMPLIANCE_CREATE: 'compliance.create',
  COMPLIANCE_EDIT: 'compliance.edit',
  COMPLIANCE_DELETE: 'compliance.delete',
  COMPLIANCE_EXPORT: 'compliance.export',
  COMPLIANCE_APPROVE: 'compliance.approve',

  OPERATIONS_VIEW: 'operations.view',
  OPERATIONS_CREATE: 'operations.create',
  OPERATIONS_EDIT: 'operations.edit',
  OPERATIONS_DELETE: 'operations.delete',
  OPERATIONS_EXPORT: 'operations.export',
  OPERATIONS_APPROVE: 'operations.approve',

  // FR-TRACE-014 — custody transfer approval. SIGN is mobile-only in practice
  // (the signing key lives in the device enclave), so the web uses VIEW to show
  // the pending queue read-only and CONFIGURE for the IT-Admin policy page.
  CUSTODY_APPROVAL_VIEW: 'custody_approval.view',
  CUSTODY_APPROVAL_SIGN: 'custody_approval.sign',
  CUSTODY_APPROVAL_CONFIGURE: 'custody_approval.configure',

  WORKFORCE_VIEW: 'workforce.view',
  WORKFORCE_CREATE: 'workforce.create',
  WORKFORCE_EDIT: 'workforce.edit',
  WORKFORCE_DELETE: 'workforce.delete',
  WORKFORCE_EXPORT: 'workforce.export',
  WORKFORCE_APPROVE: 'workforce.approve',
  WORKER_IDENTITY_REFERENCE_LOOKUP: 'worker_identity_reference.lookup',
  // Uploading the payment sheet that decides who counts as verified. Held by
  // the same three roles as the lookup, so this gates a button rather than a
  // new sight of the data.
  WORKER_IDENTITY_REFERENCE_IMPORT: 'worker_identity_reference.import',
  // FR-LBR-020 — attendance capture. Every action has its own key and no
  // workforce permission opens any of them, so gate attendance UI on these.
  ATTENDANCE_VIEW: 'attendance.view',
  ATTENDANCE_CREATE: 'attendance.create',
  ATTENDANCE_EDIT: 'attendance.edit',
  ATTENDANCE_LOCK: 'attendance.lock',
  ATTENDANCE_UNLOCK: 'attendance.unlock',

  SAFETY_VIEW: 'safety.view',
  SAFETY_CREATE: 'safety.create',
  SAFETY_EDIT: 'safety.edit',
  SAFETY_DELETE: 'safety.delete',
  SAFETY_EXPORT: 'safety.export',
  SAFETY_APPROVE: 'safety.approve',

  ENVIRONMENT_VIEW: 'environment.view',
  ENVIRONMENT_CREATE: 'environment.create',
  ENVIRONMENT_EDIT: 'environment.edit',
  ENVIRONMENT_DELETE: 'environment.delete',
  ENVIRONMENT_EXPORT: 'environment.export',
  ENVIRONMENT_APPROVE: 'environment.approve',

  INVENTORY_VIEW: 'inventory.view',
  INVENTORY_CREATE: 'inventory.create',
  INVENTORY_EDIT: 'inventory.edit',
  INVENTORY_DELETE: 'inventory.delete',
  INVENTORY_EXPORT: 'inventory.export',
  INVENTORY_APPROVE: 'inventory.approve',
  INVENTORY_REVERSE: 'inventory.reverse',
  INVENTORY_REQUISITION_APPROVE: 'inventory.approve_requisition',
  INVENTORY_REQUISITION_FULFILL: 'inventory.fulfill_requisition',
  INVENTORY_REQUISITION_CREATE: 'inventory.create_requisition',

  FINANCE_VIEW: 'finance.view',
  FINANCE_CREATE: 'finance.create',
  FINANCE_EXPORT: 'finance.export',
  FINANCE_APPROVE: 'finance.approve',
  FINANCE_PAYMENT_STATUS_MANAGE: 'finance.payment_status_manage',
  FINANCE_ACKNOWLEDGE: 'finance.acknowledge',

  PAYMENT_RUN_VIEW: 'payment_run.view',
  PAYMENT_RUN_CREATE: 'payment_run.create',
  PAYMENT_RUN_EDIT: 'payment_run.edit',
  PAYMENT_RUN_APPROVE: 'payment_run.approve',
  PAYMENT_RUN_PAY: 'payment_run.pay',

  REPORTS_VIEW: 'reports.view',
  REPORTS_CREATE: 'reports.create',
  REPORTS_EDIT: 'reports.edit',
  REPORTS_DELETE: 'reports.delete',
  REPORTS_EXPORT: 'reports.export',

  // Usage & adoption analytics. Distinct from `reports.*` (document
  // generation): these gate who-is-using-what, role activity and error
  // clustering. ANALYTICS_USER_DETAIL is separate because the leaderboard
  // names individual people.
  ANALYTICS_VIEW: 'analytics.view',
  ANALYTICS_EXPORT: 'analytics.export',
  ANALYTICS_USER_DETAIL: 'analytics.user_detail',

  AUDIT_VIEW: 'audit.view',
  AUDIT_EXPORT: 'audit.export',

  CONFIG_VIEW: 'config.view',
  CONFIG_CREATE: 'config.create',
  CONFIG_EDIT: 'config.edit',
  CONFIG_DELETE: 'config.delete',

  // User management
  USER_READ: 'user.read',
  USER_WRITE: 'user.write',
  USER_DELETE: 'user.delete',
  USER_ACTIVATE: 'user.activate',
  USER_DEACTIVATE: 'user.deactivate',
  USER_SUSPEND: 'user.suspend',
  USER_BULK_ACTION: 'user.bulk_action',

  // Department management
  DEPARTMENT_READ: 'department.read',
  DEPARTMENT_WRITE: 'department.write',
  DEPARTMENT_DELETE: 'department.delete',

  // Shift management
  // ── Milestone 2 modules (Appendix B.4) ──────────────────────────────────
  // Supply Chain and Logistics (operations.logistics)
  LOGISTICS_VIEW: 'logistics.view',
  LOGISTICS_CREATE: 'logistics.create',
  LOGISTICS_EDIT: 'logistics.edit',
  LOGISTICS_APPROVE: 'logistics.approve',

  // Procurement and Vendor Management (operations.procurement)
  PROCUREMENT_VIEW: 'procurement.view',
  PROCUREMENT_CREATE: 'procurement.create',
  PROCUREMENT_EDIT: 'procurement.edit',
  PROCUREMENT_EXPORT: 'procurement.export',
  PROCUREMENT_APPROVE: 'procurement.approve',

  // Surveying and Mapping (mining.surveying)
  SURVEYING_VIEW: 'surveying.view',
  SURVEYING_CREATE: 'surveying.create',
  SURVEYING_EDIT: 'surveying.edit',
  SURVEYING_APPROVE: 'surveying.approve',

  // Ore Processing Management (mining.ore_processing)
  ORE_PROCESSING_VIEW: 'ore_processing.view',
  ORE_PROCESSING_CREATE: 'ore_processing.create',
  ORE_PROCESSING_EDIT: 'ore_processing.edit',
  ORE_PROCESSING_APPROVE: 'ore_processing.approve',

  // Laboratory Information Management System (mining.lims)
  LIMS_VIEW: 'lims.view',
  LIMS_CREATE: 'lims.create',
  LIMS_EDIT: 'lims.edit',
  LIMS_APPROVE: 'lims.approve',

  // Geology and Resource Management (mining.geology)
  GEOLOGY_VIEW: 'geology.view',
  GEOLOGY_CREATE: 'geology.create',
  GEOLOGY_EDIT: 'geology.edit',
  GEOLOGY_DELETE: 'geology.delete',
  GEOLOGY_APPROVE: 'geology.approve',

  SHIFT_VIEW: 'shift.view',
  SHIFT_WRITE: 'shift.write',
  SHIFT_DELETE: 'shift.delete',

  WORKINGPLACE_VIEW: 'workingplace.view',
  WORKINGPLACE_CREATE: 'workingplace.create',
  WORKINGPLACE_EDIT: 'workingplace.edit',
  WORKINGPLACE_DEACTIVATE: 'workingplace.deactivate',
  WORKINGPLACETYPE_EDIT: 'workingplacetype.edit',

  PLANNINGPROFILE_VIEW: 'planningprofile.view',
  PLANNINGPROFILE_EDIT: 'planningprofile.edit',
  PLANNINGMETRICS_VIEW: 'planningmetrics.view',
  PLANNINGCODES_VIEW: 'planningcodes.view',
  PLANNINGCODES_EDIT: 'planningcodes.edit',

  PLAN_VIEW: 'plan.view',
  PLAN_CREATE: 'plan.create',
  PLAN_EDIT: 'plan.edit',
  PLAN_SUBMIT: 'plan.submit',
  PLAN_APPROVE: 'plan.approve',

  SHIFTREPORT_VIEW: 'shiftreport.view',
  SHIFTREPORT_CREATE: 'shiftreport.create',
  SHIFTREPORT_EDIT: 'shiftreport.edit',
  SHIFTREPORT_SUBMIT: 'shiftreport.submit',
  SHIFTREPORT_APPROVE: 'shiftreport.approve',

  DAILYREPORT_VIEW: 'dailyreport.view',
  DAILYREPORT_EDIT: 'dailyreport.edit',
  DAILYREPORT_APPROVE: 'dailyreport.approve',
  DAILYREPORT_REOPEN: 'dailyreport.reopen',

  VARIANCE_VIEW: 'variance.view',
  VARIANCE_CONFIGURE: 'variance.configure',

  // Financial requisitions. VIEW_ALL is separate from VIEW: a holder of VIEW
  // alone only ever sees their own requisitions.
  FINANCIAL_REQUISITION_VIEW: 'financial_requisition.view',
  FINANCIAL_REQUISITION_VIEW_ALL: 'financial_requisition.view_all',
  FINANCIAL_REQUISITION_CREATE: 'financial_requisition.create',
  FINANCIAL_REQUISITION_EDIT: 'financial_requisition.edit',
  FINANCIAL_REQUISITION_CANCEL: 'financial_requisition.cancel',
  FINANCIAL_REQUISITION_APPROVE: 'financial_requisition.approve',

  // Leadership hierarchy (organisation chart / reporting lines) — not an RBAC
  // concept. WRITE covers create, edit, and assigning a post-holder.
  LEADERSHIP_HIERARCHY_READ: 'leadership_hierarchy.read',
  LEADERSHIP_HIERARCHY_WRITE: 'leadership_hierarchy.write',
  LEADERSHIP_HIERARCHY_DELETE: 'leadership_hierarchy.delete',
  LEADERSHIP_HIERARCHY_REPARENT: 'leadership_hierarchy.reparent',
  LEADERSHIP_HIERARCHY_REORDER: 'leadership_hierarchy.reorder',

  // Pending Actions (overdue work rolled up along the reporting lines). VIEW
  // shows a person their own overdue items; VIEW_SUBTREE additionally lets
  // them see the people reporting to them, directly or at any depth.
  PENDING_ACTIONS_VIEW: 'pending_actions.view',
  PENDING_ACTIONS_VIEW_SUBTREE: 'pending_actions.view_subtree',

  // Daily activity records (activity counters, sanitation rounds, waste status)
  // and the lists they pick from. VIEW also lets a person produce the daily
  // reports built from them.
  DAILYACTIVITY_VIEW: 'dailyactivity.view',
  DAILYACTIVITY_CREATE: 'dailyactivity.create',
  DAILYACTIVITY_EDIT: 'dailyactivity.edit',
  DAILYACTIVITY_DELETE: 'dailyactivity.delete',
  DAILYACTIVITY_MANAGE_LISTS: 'dailyactivity.manage_lists',

  // Payment processing
  PAYMENT_READ: 'payment.read',
  PAYMENT_PROCESS: 'payment.process',

  // Pricing
  PRICING_RULE_READ: 'pricing_rule.read',
  PRICING_RULE_WRITE: 'pricing_rule.write',

  // Transactions
  TRANSACTION_READ: 'transaction.read',

  // Role & permission management
  ROLE_READ: 'role.read',
  ROLE_WRITE: 'role.write',
  ROLE_DELETE: 'role.delete',
  PERMISSION_READ: 'permission.read',
  PERMISSION_WRITE: 'permission.write',
  PERMISSION_DELETE: 'permission.delete',

  // Dashboard
  DASHBOARD_READ: 'dashboard.read',
  DASHBOARD_CUSTOMIZE: 'dashboard.customize',
  DASHBOARD_CONFIGURE: 'dashboard.configure',
  KPI_TARGET_VIEW: 'kpi_target.view',
  KPI_TARGET_MANAGE: 'kpi_target.manage',

  // Settings
  SETTINGS_READ: 'settings.read',
  SETTINGS_WRITE: 'settings.write',

  // Legal documents
  LEGAL_DOCUMENT_READ: 'legal_document.read',
  LEGAL_DOCUMENT_WRITE: 'legal_document.write',
  LEGAL_DOCUMENT_PUBLISH: 'legal_document.publish',

  // Announcements

  // Security

  // Notifications
  NOTIFICATION_READ: 'notification.read',
  NOTIFICATION_WRITE: 'notification.write',

  // Incident management
  INCIDENT_READ: 'incident.read',
  INCIDENT_WRITE: 'incident.write',
  INCIDENT_RESOLVE: 'incident.resolve',

  // Messaging
  MESSAGING_READ: 'messaging.read',
  MESSAGING_WRITE: 'messaging.write',

  // Tenant management (platform admin)
  TENANT_VIEW: 'tenant.view',
  TENANT_CREATE: 'tenant.create',
  TENANT_EDIT: 'tenant.edit',
  TENANT_DELETE: 'tenant.delete',

  // Platform health (platform admin)
  PLATFORM_HEALTH_VIEW: 'platform_health.view',

  // Platform configuration (platform admin)
  PLATFORM_CONFIG_VIEW: 'platform_config.view',
  PLATFORM_CONFIG_EDIT: 'platform_config.edit',

  // Support impersonation. VIEW covers the session list and audit trail; START
  // is the act of assuming another user's identity and is deliberately separate
  // so read access can be granted without it.
  IMPERSONATION_VIEW: 'impersonation.view',
  IMPERSONATION_START: 'impersonation.start',

  // Developer & API — tenant-managed API keys, webhooks, IP allowlist.
  // DELETE is separate from REVOKE because it destroys the row (and is only
  // permitted on an already-revoked key). REPLAY is separate from MANAGE
  // because a replay re-sends a real business event.
  APIKEY_VIEW: 'apikey.view',
  APIKEY_CREATE: 'apikey.create',
  APIKEY_ROTATE: 'apikey.rotate',
  APIKEY_REVOKE: 'apikey.revoke',
  APIKEY_DELETE: 'apikey.delete',

  WEBHOOK_VIEW: 'webhook.view',
  WEBHOOK_MANAGE: 'webhook.manage',
  WEBHOOK_REPLAY: 'webhook.replay',

  IP_ALLOWLIST_VIEW: 'ip_allowlist.view',
  IP_ALLOWLIST_MANAGE: 'ip_allowlist.manage',

  // Support tickets (FR14) — CREATE/VIEW-mine are effectively everyone (every
  // role that can log in can file a ticket); VIEW/ESCALATE/RESOLVE gate the
  // tenant-wide queue for IT Administrator / Mine Director / Mine Manager.
  SUPPORT_TICKET_CREATE: 'support_ticket.create',
  SUPPORT_TICKET_VIEW: 'support_ticket.view',
  SUPPORT_TICKET_ESCALATE: 'support_ticket.escalate',
  SUPPORT_TICKET_RESOLVE: 'support_ticket.resolve',

  // Visitors Induction Form. APPROVE covers both approve and reject (a single
  // Mine Manager review decision). SIGNOUT is split from CREATE because
  // closing out a visit is a distinct act from submitting/inducting/signing
  // one in. MEDICAL gates the restricted medicalConditionDetail field only.
  VISITOR_VIEW: 'visitor.view',
  VISITOR_CREATE: 'visitor.create',
  VISITOR_APPROVE: 'visitor.approve',
  VISITOR_SIGNOUT: 'visitor.signout',
  VISITOR_REVIEW: 'visitor.review',
  VISITOR_MEDICAL: 'visitor.medical',
  // Held by everyone signed in unless an admin denies them for one person.
  USER_DIRECTORY_VIEW: 'user_directory.view',
  PROFILE_EDIT: 'profile.edit',
  PROFILE_CHANGE_PASSWORD: 'profile.change_password',
  PROFILE_CHANGE_EMAIL: 'profile.change_email',
  PROFILE_VERIFY_PHONE: 'profile.verify_phone',
  PROFILE_COMPLETE_SETUP: 'profile.complete_setup',
  PROFILE_ACCEPT_TERMS: 'profile.accept_terms',
  NOTIFICATION_PREFERENCE_VIEW: 'notification_preference.view',
  NOTIFICATION_PREFERENCE_EDIT: 'notification_preference.edit',
  NOTIFICATION_PREFERENCE_RUN_DIGEST: 'notification_preference.run_digest',
  ACCOUNT_VIEW: 'account.view',
  ACCOUNT_CREATE: 'account.create',
  INCIDENT_REPORT_CREATE: 'incident_report.create',
  HAZARD_PHOTO_CREATE: 'hazard_photo.create',
  INCIDENT_TYPE_VIEW: 'incident_type.view',
  INSPECTION_TYPE_VIEW: 'inspection_type.view',
  INSPECTION_CHECKLIST_VIEW: 'inspection_checklist.view',
  RISK_CLASSIFICATION_VIEW: 'risk_classification.view',

  // Open to everyone signed in today, so everyone holds these by default.
  ALERT_VIEW: 'alert.view',
  BLASTER_CERTIFICATE_VIEW: 'blaster_certificate.view',
  BLASTING_LOG_CREATE: 'blasting_log.create',
  DEVELOPMENT_PLAN_VIEW: 'development_plan.view',
  DEVELOPMENT_SHIFT_CREATE: 'development_shift.create',
  DEVELOPMENT_SHIFT_VIEW: 'development_shift.view',
  EQUIPMENT_LOG_VIEW: 'equipment_log.view',
  FEATURE_FLAG_VIEW: 'feature_flag.view',
  HELP_CENTER_VIEW: 'help_center.view',
  KYB_CREATE: 'kyb.create',
  KYB_EDIT: 'kyb.edit',
  KYB_SUBMIT: 'kyb.submit',
  KYB_VIEW: 'kyb.view',
  LEGAL_NOTICE_VIEW: 'legal_notice.view',
  LOT_CHANGE_STATUS: 'lot.change_status',
  LOT_CREATE: 'lot.create',
  LOT_EDIT: 'lot.edit',
  LOT_VIEW: 'lot.view',
  MATERIAL_AVAILABILITY_VIEW: 'material_availability.view',
  MEU_CREATE: 'meu.create',
  MY_DASHBOARD_VIEW: 'my_dashboard.view',
  MY_REPORTING_LINE_VIEW: 'my_reporting_line.view',
  MY_SUPPORT_TICKET_VIEW: 'my_support_ticket.view',
  OFFLINE_SYNC_PULL: 'offline_sync.pull',
  OFFLINE_SYNC_VIEW: 'offline_sync.view',
  ONBOARDING_EDIT: 'onboarding.edit',
  ONBOARDING_VIEW: 'onboarding.view',
  OPERATIONS_LOOKUP_VIEW: 'operations_lookup.view',
  OPERATIONS_REPORT_VIEW: 'operations_report.view',
  PRODUCTION_LOG_VIEW: 'production_log.view',
  PRODUCTION_TARGET_VIEW: 'production_target.view',
  SHIFT_LOG_CREATE: 'shift_log.create',
  SHIFT_LOG_VIEW: 'shift_log.view',
  SIGNING_KEY_CREATE: 'signing_key.create',
  SIGNING_KEY_REVOKE: 'signing_key.revoke',
  SIGNING_KEY_VIEW: 'signing_key.view',
  SITE_VIEW: 'site.view',
  TAILINGS_CREATE: 'tailings.create',
  TAILINGS_VIEW: 'tailings.view',
  TEAM_VIEW: 'team.view',
  TEAM_LABOUR_LOG_VIEW: 'team_labour_log.view',
  TSF_CELL_CREATE: 'tsf_cell.create',
  TSF_CELL_EDIT: 'tsf_cell.edit',
  TSF_CELL_VIEW: 'tsf_cell.view',
  TUNNEL_VIEW: 'tunnel.view',
  WORKING_DAY_VIEW: 'working_day.view',
  WORKING_PLACE_LOOKUP_VIEW: 'working_place_lookup.view',

  // Guarded by a list of roles before; granted to exactly those roles.
  ACTIVITY_CREATE: 'activity.create',
  ACTIVITY_VIEW: 'activity.view',
  AUDIT_LOG_ANALYTICS_VIEW: 'audit_log_analytics.view',
  AUTH_ANALYTICS_VIEW: 'auth_analytics.view',
  BILLING_ANALYTICS_VIEW: 'billing_analytics.view',
  BLASTER_CERTIFICATE_CREATE: 'blaster_certificate.create',
  BLASTER_CERTIFICATE_EDIT: 'blaster_certificate.edit',
  BLASTER_CERTIFICATE_REVOKE: 'blaster_certificate.revoke',
  COMPANY_VIEW_VIEW: 'company_view.view',
  CUSTODY_TRANSFER_CREATE: 'custody_transfer.create',
  DEVELOPMENT_PLAN_CREATE: 'development_plan.create',
  DEVELOPMENT_PLAN_EDIT: 'development_plan.edit',
  DEVELOPMENT_SHIFT_CREATE_CORRECT: 'development_shift.create_correct',
  EQUIPMENT_LOG_CREATE: 'equipment_log.create',
  FEATURE_FLAG_ANALYTICS_VIEW: 'feature_flag_analytics.view',
  FINANCE_ANALYTICS_EXPORT: 'finance_analytics.export',
  FINANCE_ANALYTICS_VIEW_COMMISSION_CALCULATIONS: 'finance_analytics.view_commission_calculations',
  FINANCE_ANALYTICS_VIEW_COMPENSATION_PERIOD_OVER_PERIOD: 'finance_analytics.view_compensation_period_over_period',
  FINANCE_ANALYTICS_VIEW_PAYMENT_RECONCILIATION: 'finance_analytics.view_payment_reconciliation',
  FINANCE_ANALYTICS_VIEW_PAYMENT_TYPE_DISTRIBUTION: 'finance_analytics.view_payment_type_distribution',
  FINANCE_ANALYTICS_VIEW_PAYROLL_SUMMARY: 'finance_analytics.view_payroll_summary',
  FINANCE_ANALYTICS_VIEW_REVENUE: 'finance_analytics.view_revenue',
  FINANCE_ANALYTICS_VIEW_REVENUE_COST_SUMMARY: 'finance_analytics.view_revenue_cost_summary',
  FINANCE_ANALYTICS_VIEW_REVENUE_MONTHLY_DELTA: 'finance_analytics.view_revenue_monthly_delta',
  FINANCE_ANALYTICS_VIEW_STATUS_DISTRIBUTION: 'finance_analytics.view_status_distribution',
  FINANCE_ANALYTICS_VIEW_TRANSACTION_TRENDS: 'finance_analytics.view_transaction_trends',
  FINANCE_ANALYTICS_VIEW_WAGE_DISTRIBUTION: 'finance_analytics.view_wage_distribution',
  FINANCE_HISTORY_VIEW: 'finance_history.view',
  HASH_CHAIN_VERIFY: 'hash_chain.verify',
  HASH_CHAIN_VIEW_BLOCKS: 'hash_chain.view_blocks',
  HASH_CHAIN_VIEW_HEAD_DETAIL: 'hash_chain.view_head_detail',
  HASH_CHAIN_VIEW_HEADS: 'hash_chain.view_heads',
  HASH_CHAIN_VIEW_RUNS: 'hash_chain.view_runs',
  HASH_CHAIN_VIEW_STATS: 'hash_chain.view_stats',
  INCIDENTS_ANALYTICS_VIEW: 'incidents_analytics.view',
  INTEGRITY_REPORT_VIEW: 'integrity_report.view',
  INVENTORY_ANALYTICS_VIEW: 'inventory_analytics.view',
  LEVEL_CREATE: 'level.create',
  LEVEL_EDIT: 'level.edit',
  LICENSE_RUN: 'license.run',
  LOT_VIEW_CERTIFICATE: 'lot.view_certificate',
  MATERIAL_AVAILABILITY_CREATE: 'material_availability.create',
  MEU_CREATE_LABEL: 'meu.create_label',
  MEU_VIEW_CERTIFICATE: 'meu.view_certificate',
  MEU_VIEW_CHAIN: 'meu.view_chain',
  MEU_VIEW_CHAIN_HEAD: 'meu.view_chain_head',
  MEU_VIEW_DETAIL: 'meu.view_detail',
  MEU_VIEW_LIST: 'meu.view_list',
  MEU_VIEW_STATS: 'meu.view_stats',
  OPERATIONS_ANALYTICS_EDIT: 'operations_analytics.edit',
  OPERATIONS_ANALYTICS_VIEW_BLASTING_TRENDS: 'operations_analytics.view_blasting_trends',
  OPERATIONS_ANALYTICS_VIEW_CURRENT_SHIFT_BLASTING: 'operations_analytics.view_current_shift_blasting',
  OPERATIONS_ANALYTICS_VIEW_CUSTODY_INTEGRITY: 'operations_analytics.view_custody_integrity',
  OPERATIONS_ANALYTICS_VIEW_DEVELOPMENT_PROGRESS: 'operations_analytics.view_development_progress',
  OPERATIONS_ANALYTICS_VIEW_EXECUTIVE_PRODUCTION_OVERVIEW: 'operations_analytics.view_executive_production_overview',
  OPERATIONS_ANALYTICS_VIEW_MEU_TRACEABILITY: 'operations_analytics.view_meu_traceability',
  OPERATIONS_ANALYTICS_VIEW_MISFIRE_SUMMARY: 'operations_analytics.view_misfire_summary',
  OPERATIONS_ANALYTICS_VIEW_OPERATIONAL_EFFICIENCY: 'operations_analytics.view_operational_efficiency',
  OPERATIONS_ANALYTICS_VIEW_OVERVIEW: 'operations_analytics.view_overview',
  OPERATIONS_ANALYTICS_VIEW_PRODUCTION_BREAKDOWN: 'operations_analytics.view_production_breakdown',
  OPERATIONS_ANALYTICS_VIEW_PRODUCTION_DECLARATION: 'operations_analytics.view_production_declaration',
  OPERATIONS_ANALYTICS_VIEW_PRODUCTION_TRENDS: 'operations_analytics.view_production_trends',
  OPERATIONS_ANALYTICS_VIEW_TEAMS_BY_TUNNEL: 'operations_analytics.view_teams_by_tunnel',
  OPERATIONS_ANALYTICS_VIEW_TOP_PERFORMERS: 'operations_analytics.view_top_performers',
  OPERATIONS_ANALYTICS_VIEW_ZONE_PRODUCTION: 'operations_analytics.view_zone_production',
  PAYMENT_ANALYTICS_VIEW: 'payment_analytics.view',
  PAYMENT_BATCH_CREATE: 'payment_batch.create',
  PAYMENT_BATCH_VIEW: 'payment_batch.view',
  PAYMENT_BATCH_DISCARD: 'payment_batch.discard',
  PAYMENT_BATCH_SUBMIT_FOR_REVIEW: 'payment_batch.submit_for_review',
  PAYMENT_BATCH_SUBMIT: 'payment_batch.submit',
  PAYMENT_BATCH_APPROVE: 'payment_batch.approve',
  PAYMENT_BATCH_REJECT: 'payment_batch.reject',
  PAYMENT_BATCH_ACKNOWLEDGE: 'payment_batch.acknowledge',
  PAYMENT_BATCH_EXPORT: 'payment_batch.export',
  PAYMENT_HISTORY_VIEW: 'payment_history.view',
  PAYMENT_RECONCILIATION_VIEW: 'payment_reconciliation.view',
  PAYMENT_TRANSACTION_VIEW: 'payment_transaction.view',
  PERMISSION_ANALYTICS_VIEW: 'permission_analytics.view',
  PLATFORM_AUDIT_CREATE: 'platform_audit.create',
  PLATFORM_AUDIT_VIEW: 'platform_audit.view',
  PLATFORM_HEALTH_ANALYTICS_VIEW: 'platform_health_analytics.view',
  PRICING_MODEL_CREATE: 'pricing_model.create',
  PRICING_MODEL_DELETE: 'pricing_model.delete',
  PRICING_MODEL_EDIT: 'pricing_model.edit',
  PRICING_MODEL_VIEW: 'pricing_model.view',
  PRICING_RULE_CREATE: 'pricing_rule.create',
  PRICING_RULE_DELETE: 'pricing_rule.delete',
  PRICING_RULE_EDIT: 'pricing_rule.edit',
  PRICING_RULE_VIEW: 'pricing_rule.view',
  PRODUCTION_LOG_CREATE_AMEND: 'production_log.create_amend',
  PRODUCTION_LOG_CREATE_ITEM: 'production_log.create_item',
  PRODUCTION_LOG_SUBMIT: 'production_log.submit',
  PRODUCTION_LOG_VERIFY: 'production_log.verify',
  PRODUCTION_TARGET_CREATE: 'production_target.create',
  PRODUCTION_TARGET_DELETE: 'production_target.delete',
  REPORT_BUILDER_ANALYTICS_VIEW: 'report_builder_analytics.view',
  REPORTING_ANALYTICS_VIEW: 'reporting_analytics.view',
  WORKER_ROLE_CREATE: 'worker_role.create',
  WORKER_ROLE_EDIT: 'worker_role.edit',
  ROLE_ANALYTICS_VIEW: 'role_analytics.view',
  SHIFT_LOG_CREATE_CLOSE: 'shift_log.create_close',
  SHIFT_LOG_EDIT: 'shift_log.edit',
  SITE_CREATE: 'site.create',
  SITE_EDIT: 'site.edit',
  STORAGE_ANALYTICS_VIEW: 'storage_analytics.view',
  SYSTEM_INCIDENT_CHANGE_STATUS: 'system_incident.change_status',
  SYSTEM_INCIDENT_RESOLVE: 'system_incident.resolve',
  SYSTEM_INCIDENT_VIEW: 'system_incident.view',
  TEAM_CREATE: 'team.create',
  TEAM_EDIT: 'team.edit',
  TEAM_LABOUR_LOG_CREATE: 'team_labour_log.create',
  TENANT_ANALYTICS_VIEW: 'tenant_analytics.view',
  TUNNEL_CREATE_ITEM: 'tunnel.create_item',
  TUNNEL_CREATE_REACTIVATE: 'tunnel.create_reactivate',
  TUNNEL_EDIT: 'tunnel.edit',
  USER_CONVERSATION_CREATE: 'user_conversation.create',
  USER_ANALYTICS_VIEW: 'user_analytics.view',
  WORKER_EDIT: 'worker.edit',
  WORKFORCE_ANALYTICS_VIEW_ATTENDANCE_OVERVIEW: 'workforce_analytics.view_attendance_overview',
  WORKFORCE_ANALYTICS_VIEW_ATTENDANCE_TRENDS: 'workforce_analytics.view_attendance_trends',
  WORKFORCE_ANALYTICS_VIEW_OVERVIEW: 'workforce_analytics.view_overview',
  WORKFORCE_ANALYTICS_VIEW_PAYROLL_TRENDS: 'workforce_analytics.view_payroll_trends',
  WORKFORCE_ANALYTICS_VIEW_WORKER_COMPENSATION_SUMMARY: 'workforce_analytics.view_worker_compensation_summary',
  WORKFORCE_ANALYTICS_VIEW_WORKFORCE_PRODUCTIVITY: 'workforce_analytics.view_workforce_productivity',
  WORKFORCE_ANALYTICS_VIEW_WORKFORCE_SUMMARY: 'workforce_analytics.view_workforce_summary',
  WORKING_DAY_CREATE: 'working_day.create',
  ZONE_CREATE: 'zone.create',
  ZONE_EDIT: 'zone.edit',
  ZONE_OWNERSHIP_APPROVE: 'zone_ownership.approve',
  ZONE_OWNERSHIP_CREATE_REVENUE_AGREEMENT: 'zone_ownership.create_revenue_agreement',
  ZONE_OWNERSHIP_CREATE_SUBMIT: 'zone_ownership.create_submit',
  ZONE_OWNERSHIP_REVOKE: 'zone_ownership.revoke',
  ZONE_OWNERSHIP_VIEW_LIST: 'zone_ownership.view_list',
  ZONE_OWNERSHIP_VIEW_REVENUE_AGREEMENTS: 'zone_ownership.view_revenue_agreements',
  ALERT_EVENT_ACKNOWLEDGE: 'alert_event.acknowledge',
  ALERT_RULE_EDIT: 'alert_rule.edit',
  ALERT_RULE_EDIT_COMPANY_DEFAULT: 'alert_rule.edit_company_default',
  ALERT_RULE_SITE_OVERRIDE_DELETE: 'alert_rule_site_override.delete',
  DAILY_ACTIVITY_CODE_LIST_CREATE: 'daily_activity_code_list.create',
  DAILY_ACTIVITY_CODE_LIST_EDIT: 'daily_activity_code_list.edit',
  DAILY_ACTIVITY_CODE_LIST_DEACTIVATE: 'daily_activity_code_list.deactivate',
  DAILY_ACTIVITY_CODE_LIST_ACTIVATE: 'daily_activity_code_list.activate',
  DAILY_ACTIVITY_CODE_LIST_DELETE: 'daily_activity_code_list.delete',
  DELAY_EVENT_CREATE: 'delay_event.create',
  DELAY_EVENT_EDIT: 'delay_event.edit',
  DELAY_EVENT_DELETE: 'delay_event.delete',
  WEBHOOK_CREATE: 'webhook.create',
  WEBHOOK_EDIT: 'webhook.edit',
  WEBHOOK_ROTATE_SECRET: 'webhook.rotate_secret',
  WEBHOOK_TEST: 'webhook.test',
  WEBHOOK_DELETE: 'webhook.delete',
  FINANCIAL_REQUISITION_SUBMIT: 'financial_requisition.submit',
  FINANCIAL_REQUISITION_DELETE: 'financial_requisition.delete',
  FINANCIAL_REQUISITION_DOCUMENT_CREATE: 'financial_requisition_document.create',
  FINANCIAL_REQUISITION_DOCUMENT_DELETE: 'financial_requisition_document.delete',
  PAYMENT_RUN_LINE_CONFIRM: 'payment_run_line.confirm',
  PAYMENT_RUN_LINE_FAIL: 'payment_run_line.fail',
  PAYMENT_RUN_LINE_EXCLUDE: 'payment_run_line.exclude',
  PAYMENT_RUN_COMPLETE: 'payment_run.complete',
  FUEL_ISSUE_CREATE: 'fuel_issue.create',
  FUEL_TANK_DIP_CREATE: 'fuel_tank_dip.create',
  GRADE_SAMPLE_CREATE: 'grade_sample.create',
  BULK_IMPORT_VIEW: 'bulk_import.view',
  BULK_IMPORT_CHECK: 'bulk_import.check',
  BULK_IMPORT_CONFIRM: 'bulk_import.confirm',
  INVENTORY_MATERIAL_CATEGORY_CREATE: 'inventory_material_category.create',
  INVENTORY_MATERIAL_CATEGORY_EDIT: 'inventory_material_category.edit',
  INVENTORY_MATERIAL_CATEGORY_TOGGLE_STATUS: 'inventory_material_category.toggle_status',
  INVENTORY_EQUIPMENT_TYPE_CREATE: 'inventory_equipment_type.create',
  INVENTORY_EQUIPMENT_TYPE_EDIT: 'inventory_equipment_type.edit',
  INVENTORY_EQUIPMENT_TYPE_TOGGLE_STATUS: 'inventory_equipment_type.toggle_status',
  INVENTORY_MATERIAL_CREATE: 'inventory_material.create',
  INVENTORY_MATERIAL_EDIT: 'inventory_material.edit',
  INVENTORY_EQUIPMENT_CREATE: 'inventory_equipment.create',
  INVENTORY_EQUIPMENT_EDIT: 'inventory_equipment.edit',
  INVENTORY_EQUIPMENT_CERTIFY: 'inventory_equipment.certify',
  INVENTORY_EQUIPMENT_ASSIGN: 'inventory_equipment.assign',
  INVENTORY_ORE_STOCKPILE_CREATE: 'inventory_ore_stockpile.create',
  INVENTORY_ORE_STOCKPILE_EDIT: 'inventory_ore_stockpile.edit',
  INVENTORY_STOCK_MOVEMENT_CREATE: 'inventory_stock_movement.create',
  STOCK_COUNT_CREATE: 'stock_count.create',
  STOCK_COUNT_LINE_EDIT: 'stock_count_line.edit',
  STOCK_COUNT_LINE_DELETE: 'stock_count_line.delete',
  STOCK_COUNT_ABANDON: 'stock_count.abandon',
  STOCKPILE_MOVEMENT_CREATE: 'stockpile_movement.create',
  UNIT_CREATE: 'unit.create',
  UNIT_EDIT: 'unit.edit',
  UNIT_TOGGLE_STATUS: 'unit.toggle_status',
  LOGISTICS_CARRIER_CREATE: 'logistics_carrier.create',
  LOGISTICS_CARRIER_EDIT: 'logistics_carrier.edit',
  LOGISTICS_CARRIER_VEHICLE_CREATE: 'logistics_carrier_vehicle.create',
  LOGISTICS_CARRIER_DOCUMENT_CREATE: 'logistics_carrier_document.create',
  LOGISTICS_DISPATCH_CREATE: 'logistics_dispatch.create',
  LOGISTICS_DISPATCH_DEPART: 'logistics_dispatch.depart',
  LOGISTICS_DISPATCH_RECEIVE: 'logistics_dispatch.receive',
  LOGISTICS_DISPATCH_CUSTODY_EVENT_CREATE: 'logistics_dispatch_custody_event.create',
  LOGISTICS_DISPATCH_WAYBILL_CREATE: 'logistics_dispatch_waybill.create',
  LOGISTICS_WAYBILL_SIGN: 'logistics_waybill.sign',
  LOGISTICS_DISPATCH_DISCREPANCY_CREATE: 'logistics_dispatch_discrepancy.create',
  FORECAST_OVERRIDE_CREATE: 'forecast_override.create',
  VARIANCE_THRESHOLD_CREATE: 'variance_threshold.create',
  TEAM_WORKER_CREATE: 'team_worker.create',
  TEAM_WORKER_DELETE: 'team_worker.delete',
  LOT_SPLIT: 'lot.split',
  LOT_MERGE: 'lot.merge',
  EQUIPMENT_METER_RESET_CREATE: 'equipment_meter_reset.create',
  FINANCIAL_YEAR_EDIT: 'financial_year.edit',
  PLAN_DAILY_TARGET_EDIT: 'plan_daily_target.edit',
  PLAN_DAILY_TARGET_DELETE: 'plan_daily_target.delete',
  PLAN_DAILY_TARGET_GENERATE: 'plan_daily_target.generate',
  PLAN_ROLL_UP_ROLLUP_CHECK: 'plan_roll_up.rollup_check',
  PLAN_ROLL_UP_WAIVER_CREATE: 'plan_roll_up_waiver.create',
  PLAN_TARGET_EDIT: 'plan_target.edit',
  PLAN_TARGET_CREATE: 'plan_target.create',
  PLAN_TARGET_DELETE: 'plan_target.delete',
  PLAN_VERSION_EDIT: 'plan_version.edit',
  PLAN_VERSION_APPROVE: 'plan_version.approve',
  PLAN_VERSION_REJECT: 'plan_version.reject',
  PLANNING_CODE_LIST_CREATE: 'planning_code_list.create',
  PLANNING_CODE_LIST_EDIT: 'planning_code_list.edit',
  PLANNING_CODE_LIST_DEACTIVATE: 'planning_code_list.deactivate',
  PLANNING_CODE_LIST_ACTIVATE: 'planning_code_list.activate',
  PLANNING_CODE_LIST_DELETE: 'planning_code_list.delete',
  SITE_CALENDAR_PATTERN_EDIT: 'site_calendar_pattern.edit',
  SITE_CALENDAR_EXCEPTION_CREATE: 'site_calendar_exception.create',
  SITE_CALENDAR_EXCEPTION_DELETE: 'site_calendar_exception.delete',
  SITE_PLANNING_PROFILE_CREATE: 'site_planning_profile.create',
  SITE_PLANNING_PROFILE_EDIT: 'site_planning_profile.edit',
  SITE_PLANNING_PROFILE_COMMODITY_CREATE: 'site_planning_profile_commodity.create',
  SITE_PLANNING_PROFILE_COMMODITY_EDIT: 'site_planning_profile_commodity.edit',
  SITE_PLANNING_PROFILE_USD_RATE_CREATE: 'site_planning_profile_usd_rate.create',
  SHIFT_REPORT_CREATE: 'shift_report.create',
  SHIFT_REPORT_EDIT: 'shift_report.edit',
  SHIFT_SAFETY_EDIT: 'shift_safety.edit',
  FEATURE_FLAG_TENANT_RESTRICT: 'feature_flag_tenant.restrict',
  FEATURE_FLAG_TENANT_UNRESTRICT: 'feature_flag_tenant.unrestrict',
  FEATURE_FLAG_USER_RESTRICT: 'feature_flag_user.restrict',
  FEATURE_FLAG_USER_OVERRIDE: 'feature_flag_user.override',
  TENANT_SETTING_EDIT: 'tenant_setting.edit',
  BRANDING_ASSET_CREATE: 'branding_asset.create',
  SETTINGS_PREVIEW_EDIT: 'settings_preview.edit',
  SETTINGS_PREVIEW_PUBLISH: 'settings_preview.publish',
  SETTINGS_PREVIEW_DELETE: 'settings_preview.delete',
  TENANT_SETTING_DELETE: 'tenant_setting.delete',
  TENANT_PROFILE_EDIT: 'tenant_profile.edit',
  TENANT_PROFILE_SUSPEND: 'tenant_profile.suspend',
  TENANT_PROFILE_ACTIVATE: 'tenant_profile.activate',
  TENANT_PROFILE_MAINTENANCE: 'tenant_profile.maintenance',
  HIERARCHY_LABEL_EDIT: 'hierarchy_label.edit',
  WORKFLOW_EDIT: 'workflow.edit',
  WORKFLOW_RESET_DEFAULTS: 'workflow.reset_defaults',
  WORKFLOW_REVERT: 'workflow.revert',
  CUSTODY_APPROVAL_POLICY_VIEW: 'custody_approval_policy.view',
  CUSTODY_APPROVAL_POLICY_EDIT: 'custody_approval_policy.edit',
  CUSTODY_APPROVAL_POLICY_VIEW_HISTORY: 'custody_approval_policy.view_history',
  CUSTODY_APPROVAL_POLICY_REVERT: 'custody_approval_policy.revert',
  CUSTODY_APPROVAL_POLICY_RESET_DEFAULTS: 'custody_approval_policy.reset_defaults',
  CUSTOM_DOMAIN_VERIFY: 'custom_domain.verify',
  VISITOR_SIGN_IN: 'visitor.sign_in',
  VISITOR_SIGNED_FORM: 'visitor.signed_form',
  WORKFORCE_CODE_LIST_CREATE: 'workforce_code_list.create',
  WORKFORCE_CODE_LIST_EDIT: 'workforce_code_list.edit',
  WORKFORCE_CODE_LIST_DEACTIVATE: 'workforce_code_list.deactivate',
  WORKFORCE_CODE_LIST_ACTIVATE: 'workforce_code_list.activate',
  WORKFORCE_CODE_LIST_DELETE: 'workforce_code_list.delete',
  WORKER_CREATE: 'worker.create',
  WORKER_UPLOAD_CREATE: 'worker_upload.create',
  WORKER_IMPORT_VIEW: 'worker_import.view',
  WORKER_IMPORT_CREATE: 'worker_import.create',
  WORKER_VERIFY: 'worker.verify',
  ATTENDANCE_SESSION_WORKER_CREATE: 'attendance_session_worker.create',
  ATTENDANCE_SESSION_CHECK_IN: 'attendance_session.check_in',
  ATTENDANCE_SESSION_CHECK_OUT: 'attendance_session.check_out',
  ATTENDANCE_SESSION_WORKER_DELETE: 'attendance_session_worker.delete',
  ATTENDANCE_SESSION_CLOSE: 'attendance_session.close',
  COMPENSATION_RULE_CREATE: 'compensation_rule.create',
  COMPENSATION_RULE_EDIT: 'compensation_rule.edit',
  SUBCONTRACTOR_CREATE: 'subcontractor.create',
  PAYMENT_BATCH_RECORD_FAILURE: 'payment_batch.record_failure',
  PAYMENT_BATCH_MARK_PAID: 'payment_batch.mark_paid',
  PAYMENT_BATCH_RETRY_FAILED: 'payment_batch.retry_failed',
  PAYMENT_BATCH_UPDATE_ITEM_STATUS: 'payment_batch.update_item_status',
  WORKFORCE_SHIFT_CREATE: 'workforce_shift.create',
  WORKFORCE_SHIFT_EDIT: 'workforce_shift.edit',
  WORKFORCE_SHIFT_DELETE: 'workforce_shift.delete',
  WORKING_PLACE_TYPE_CREATE: 'working_place_type.create',
  WORKING_PLACE_TYPE_EDIT: 'working_place_type.edit',
  WORKING_PLACE_TYPE_DEACTIVATE: 'working_place_type.deactivate',
  WORKING_PLACE_TYPE_ACTIVATE: 'working_place_type.activate',
  MATERIAL_RECONCILIATION_VIEW: 'material_reconciliation.view',
  BULK_IMPORT_EXPORT: 'bulk_import.export',
  INVENTORY_MATERIAL_CATEGORY_VIEW: 'inventory_material_category.view',
  INVENTORY_MATERIAL_CATEGORY_DELETE: 'inventory_material_category.delete',
  INVENTORY_EQUIPMENT_TYPE_VIEW: 'inventory_equipment_type.view',
  INVENTORY_EQUIPMENT_TYPE_DELETE: 'inventory_equipment_type.delete',
  INVENTORY_OVERVIEW_VIEW: 'inventory_overview.view',
  INVENTORY_EQUIPMENT_VIEW: 'inventory_equipment.view',
  INVENTORY_MATERIAL_VIEW: 'inventory_material.view',
  INVENTORY_MATERIAL_EXPORT: 'inventory_material.export',
  INVENTORY_MATERIAL_DELETE: 'inventory_material.delete',
  INVENTORY_EQUIPMENT_EXPORT: 'inventory_equipment.export',
  INVENTORY_EQUIPMENT_DELETE: 'inventory_equipment.delete',
  INVENTORY_ORE_STOCKPILE_EXPORT: 'inventory_ore_stockpile.export',
  INVENTORY_ORE_STOCKPILE_VIEW: 'inventory_ore_stockpile.view',
  INVENTORY_ORE_STOCKPILE_DELETE: 'inventory_ore_stockpile.delete',
  INVENTORY_STOCK_MOVEMENT_REVERSE: 'inventory_stock_movement.reverse',
  INVENTORY_STOCK_MOVEMENT_VIEW: 'inventory_stock_movement.view',
  INVENTORY_STOCK_ON_HAND_VIEW: 'inventory_stock_on_hand.view',
  INVENTORY_STOCK_MOVEMENT_EXPORT: 'inventory_stock_movement.export',
  MATERIAL_REQUISITION_CREATE: 'material_requisition.create',
  MATERIAL_REQUISITION_VIEW: 'material_requisition.view',
  MATERIAL_REQUISITION_EDIT: 'material_requisition.edit',
  MATERIAL_REQUISITION_DELETE: 'material_requisition.delete',
  MATERIAL_REQUISITION_APPROVE: 'material_requisition.approve',
  MATERIAL_REQUISITION_REJECT: 'material_requisition.reject',
  MATERIAL_REQUISITION_FULFILL: 'material_requisition.fulfill',
  STOCK_COUNT_VIEW: 'stock_count.view',
  STOCK_COUNT_CONFIRM: 'stock_count.confirm',
  STOCKPILE_MOVEMENT_VIEW: 'stockpile_movement.view',
  UNIT_VIEW: 'unit.view',
  UNIT_DELETE: 'unit.delete',
  COMPENSATION_RULE_VIEW: 'compensation_rule.view',
  COMPENSATION_RULE_APPROVE: 'compensation_rule.approve',
  COMPENSATION_RULE_REJECT: 'compensation_rule.reject',
  COMPENSATION_RULE_TOGGLE: 'compensation_rule.toggle',
  ALERT_EVENT_VIEW: 'alert_event.view',
  ALERT_RULE_VIEW: 'alert_rule.view',
  COMPLIANCE_LICENSE_CREATE: 'compliance_license.create',
  COMPLIANCE_LICENSE_VIEW: 'compliance_license.view',
  COMPLIANCE_LICENSE_EDIT: 'compliance_license.edit',
  COMPLIANCE_LICENSE_RENEW: 'compliance_license.renew',
  COMPLIANCE_LICENSE_OVERRIDE: 'compliance_license.override',
  COMPLIANCE_LICENSE_DELETE: 'compliance_license.delete',
  COMPLIANCE_REQUIREMENT_CREATE: 'compliance_requirement.create',
  COMPLIANCE_REQUIREMENT_EDIT: 'compliance_requirement.edit',
  COMPLIANCE_REQUIREMENT_DELETE: 'compliance_requirement.delete',
  COMPLIANCE_REQUIREMENT_VIEW: 'compliance_requirement.view',
  COMPLIANCE_REQUIREMENT_STATUS: 'compliance_requirement.status',
  COMPLIANCE_REQUIREMENT_ISSUE_CREATE: 'compliance_requirement_issue.create',
  COMPLIANCE_REQUIREMENT_ISSUE_VIEW: 'compliance_requirement_issue.view',
  COMPLIANCE_ISSUE_VIEW: 'compliance_issue.view',
  COMPLIANCE_ISSUE_EDIT: 'compliance_issue.edit',
  COMPLIANCE_COMMITMENT_CREATE: 'compliance_commitment.create',
  COMPLIANCE_COMMITMENT_VIEW: 'compliance_commitment.view',
  COMPLIANCE_COMMITMENT_EDIT: 'compliance_commitment.edit',
  COMPLIANCE_COMMITMENT_DELETE: 'compliance_commitment.delete',
  COMPLIANCE_FEEDBACK_CREATE: 'compliance_feedback.create',
  COMPLIANCE_FEEDBACK_VIEW: 'compliance_feedback.view',
  COMPLIANCE_FEEDBACK_EDIT: 'compliance_feedback.edit',
  COMPLIANCE_INSPECTION_VIEW: 'compliance_inspection.view',
  COMPLIANCE_INSPECTION_CREATE: 'compliance_inspection.create',
  COMPLIANCE_INSPECTION_RESOLUTION: 'compliance_inspection.resolution',
  COMPLIANCE_DOCUMENT_CREATE: 'compliance_document.create',
  COMPLIANCE_DOCUMENT_VIEW: 'compliance_document.view',
  COMPLIANCE_DOCUMENT_DOWNLOAD: 'compliance_document.download',
  COMPLIANCE_DOCUMENT_DELETE: 'compliance_document.delete',
  COMPLIANCE_REPORT_PREVIEW: 'compliance_report.preview',
  COMPLIANCE_REPORT_CREATE: 'compliance_report.create',
  DAILY_ACTIVITY_CODE_LIST_VIEW: 'daily_activity_code_list.view',
  DELAY_EVENT_VIEW: 'delay_event.view',
  ENVIRONMENT_PARAMETER_CREATE: 'environment_parameter.create',
  ENVIRONMENT_PARAMETER_VIEW: 'environment_parameter.view',
  ENVIRONMENT_PARAMETER_EDIT: 'environment_parameter.edit',
  ENVIRONMENT_PARAMETER_DELETE: 'environment_parameter.delete',
  ENVIRONMENT_MONITORING_RECORD_CREATE: 'environment_monitoring_record.create',
  ENVIRONMENT_MONITORING_RECORD_VIEW: 'environment_monitoring_record.view',
  ENVIRONMENT_MONITORING_RECORD_ACKNOWLEDGE: 'environment_monitoring_record.acknowledge',
  ENVIRONMENT_MONITORING_RECORD_RESOLVE: 'environment_monitoring_record.resolve',
  ENVIRONMENT_INCIDENT_CREATE: 'environment_incident.create',
  ENVIRONMENT_INCIDENT_VIEW: 'environment_incident.view',
  ENVIRONMENT_INCIDENT_EDIT: 'environment_incident.edit',
  ENVIRONMENT_COMPLIANCE_RECORD_VIEW: 'environment_compliance_record.view',
  ENVIRONMENT_COMPLIANCE_RECORD_EVIDENCE: 'environment_compliance_record.evidence',
  FUEL_ISSUE_VIEW: 'fuel_issue.view',
  FUEL_TANK_DIP_VIEW: 'fuel_tank_dip.view',
  GEOLOGY_DRILL_HOLE_CREATE: 'geology_drill_hole.create',
  GEOLOGY_DRILL_HOLE_VIEW: 'geology_drill_hole.view',
  GEOLOGY_DRILL_HOLE_EDIT: 'geology_drill_hole.edit',
  GEOLOGY_DRILL_HOLE_DELETE: 'geology_drill_hole.delete',
  GEOLOGY_SAMPLE_CREATE: 'geology_sample.create',
  GEOLOGY_SAMPLE_DISPATCH: 'geology_sample.dispatch',
  GEOLOGY_BLOCK_CREATE: 'geology_block.create',
  GEOLOGY_BLOCK_VIEW: 'geology_block.view',
  GEOLOGY_BLOCK_EDIT: 'geology_block.edit',
  GEOLOGY_STATEMENT_CREATE: 'geology_statement.create',
  GEOLOGY_STATEMENT_VIEW: 'geology_statement.view',
  GEOLOGY_MAP_RECORD_CREATE: 'geology_map_record.create',
  GEOLOGY_MAP_RECORD_VIEW: 'geology_map_record.view',
  GRADE_SAMPLE_VIEW: 'grade_sample.view',
  LIMS_SAMPLE_CREATE: 'lims_sample.create',
  LIMS_SAMPLE_VIEW: 'lims_sample.view',
  LIMS_SAMPLE_STAGE: 'lims_sample.stage',
  LIMS_SAMPLE_CUSTODY: 'lims_sample.custody',
  LIMS_SAMPLE_RESULT_CREATE: 'lims_sample_result.create',
  LIMS_SAMPLE_RESULT_VIEW: 'lims_sample_result.view',
  LIMS_BATCH_QAQC: 'lims_batch.qaqc',
  LIMS_IMPORT_CREATE: 'lims_import.create',
  LIMS_CERTIFICATE_CREATE: 'lims_certificate.create',
  LIMS_CERTIFICATE_VIEW: 'lims_certificate.view',
  LOGISTICS_CARRIER_VIEW: 'logistics_carrier.view',
  LOGISTICS_CARRIER_VEHICLE_VIEW: 'logistics_carrier_vehicle.view',
  LOGISTICS_CARRIER_DOCUMENT_VIEW: 'logistics_carrier_document.view',
  LOGISTICS_DISPATCH_VIEW: 'logistics_dispatch.view',
  LOGISTICS_DISPATCH_CUSTODY_CHAIN: 'logistics_dispatch.custody_chain',
  LOGISTICS_DISPATCH_WAYBILL_VIEW: 'logistics_dispatch_waybill.view',
  LOGISTICS_DISCREPANCY_RESOLVE: 'logistics_discrepancy.resolve',
  OPERATIONS_SITE_DELETE: 'operations_site.delete',
  OPERATIONS_ZONE_DELETE: 'operations_zone.delete',
  OPERATIONS_LEVEL_DELETE: 'operations_level.delete',
  OPERATIONS_TUNNEL_DELETE: 'operations_tunnel.delete',
  OPERATIONS_TEAM_DELETE: 'operations_team.delete',
  OPERATIONS_PLACEMENT_VIEW: 'operations_placement.view',
  OPERATIONS_PLACEMENT_TRACEABILITY: 'operations_placement.traceability',
  OPERATIONS_DEVELOPMENT_PLAN_APPROVE: 'operations_development_plan.approve',
  ORE_PROCESSING_PLANT_CREATE: 'ore_processing_plant.create',
  ORE_PROCESSING_PLANT_VIEW: 'ore_processing_plant.view',
  ORE_PROCESSING_PLANT_EDIT: 'ore_processing_plant.edit',
  ORE_PROCESSING_PLANT_STAGE_CREATE: 'ore_processing_plant_stage.create',
  ORE_PROCESSING_PLANT_STAGE_VIEW: 'ore_processing_plant_stage.view',
  ORE_PROCESSING_PLANT_PERFORMANCE: 'ore_processing_plant.performance',
  ORE_PROCESSING_BATCH_CREATE: 'ore_processing_batch.create',
  ORE_PROCESSING_BATCH_VIEW: 'ore_processing_batch.view',
  ORE_PROCESSING_BATCH_COMPLETE: 'ore_processing_batch.complete',
  ORE_PROCESSING_BATCH_EXPORT_READINESS: 'ore_processing_batch.export_readiness',
  FINANCIAL_YEAR_VIEW: 'financial_year.view',
  PLAN_DAILY_TARGET_VIEW: 'plan_daily_target.view',
  PLAN_TARGET_VIEW: 'plan_target.view',
  PLAN_VERSION_VIEW: 'plan_version.view',
  PROCUREMENT_VENDOR_CREATE: 'procurement_vendor.create',
  PROCUREMENT_VENDOR_VIEW: 'procurement_vendor.view',
  PROCUREMENT_VENDOR_EDIT: 'procurement_vendor.edit',
  PROCUREMENT_VENDOR_DOCUMENT_CREATE: 'procurement_vendor_document.create',
  PROCUREMENT_VENDOR_DOCUMENT_VIEW: 'procurement_vendor_document.view',
  PROCUREMENT_APPROVAL_THRESHOLD_CREATE: 'procurement_approval_threshold.create',
  PROCUREMENT_APPROVAL_THRESHOLD_VIEW: 'procurement_approval_threshold.view',
  PROCUREMENT_PURCHASE_ORDER_CREATE: 'procurement_purchase_order.create',
  PROCUREMENT_PURCHASE_ORDER_VIEW: 'procurement_purchase_order.view',
  PROCUREMENT_PURCHASE_ORDER_APPROVE: 'procurement_purchase_order.approve',
  PROCUREMENT_PURCHASE_ORDER_RECEIPT_CREATE: 'procurement_purchase_order_receipt.create',
  INCIDENT_TYPE_CONFIG_VIEW: 'incident_type_config.view',
  INCIDENT_TYPE_CONFIG_CREATE: 'incident_type_config.create',
  INCIDENT_TYPE_CONFIG_EDIT: 'incident_type_config.edit',
  INCIDENT_TYPE_CONFIG_DELETE: 'incident_type_config.delete',
  INSPECTION_CHECKLIST_TEMPLATE_VIEW: 'inspection_checklist_template.view',
  INSPECTION_CHECKLIST_TEMPLATE_CREATE: 'inspection_checklist_template.create',
  INSPECTION_CHECKLIST_TEMPLATE_EDIT: 'inspection_checklist_template.edit',
  INSPECTION_CHECKLIST_TEMPLATE_DELETE: 'inspection_checklist_template.delete',
  INSPECTION_TYPE_CONFIG_VIEW: 'inspection_type_config.view',
  INSPECTION_TYPE_CONFIG_CREATE: 'inspection_type_config.create',
  INSPECTION_TYPE_CONFIG_EDIT: 'inspection_type_config.edit',
  INSPECTION_TYPE_CONFIG_DELETE: 'inspection_type_config.delete',
  RISK_CLASSIFICATION_CONFIG_EDIT: 'risk_classification_config.edit',
  SAFETY_INSPECTION_CREATE: 'safety_inspection.create',
  SAFETY_INSPECTION_VIEW: 'safety_inspection.view',
  SAFETY_INSPECTION_ACKNOWLEDGE_UNSATISFACTORY: 'safety_inspection.acknowledge_unsatisfactory',
  SAFETY_INSPECTION_AMEND: 'safety_inspection.amend',
  SAFETY_INSPECTION_SCHEDULE_CREATE: 'safety_inspection_schedule.create',
  SAFETY_INSPECTION_SCHEDULE_VIEW: 'safety_inspection_schedule.view',
  SAFETY_HAZARD_CREATE: 'safety_hazard.create',
  SAFETY_HAZARD_VIEW: 'safety_hazard.view',
  SAFETY_HAZARD_TRIAGE: 'safety_hazard.triage',
  SAFETY_CORRECTIVE_ACTION_CREATE: 'safety_corrective_action.create',
  SAFETY_CORRECTIVE_ACTION_VIEW: 'safety_corrective_action.view',
  SAFETY_CORRECTIVE_ACTION_STATUS: 'safety_corrective_action.status',
  SHIFT_REPORT_VIEW: 'shift_report.view',
  SHIFT_SAFETY_VIEW: 'shift_safety.view',
  SURVEYING_SURVEY_CREATE: 'surveying_survey.create',
  SURVEYING_SURVEY_VIEW: 'surveying_survey.view',
  SURVEYING_SURVEY_EDIT: 'surveying_survey.edit',
  SURVEYING_SURVEY_FILE_CREATE: 'surveying_survey_file.create',
  SURVEYING_SURVEY_RECONCILE: 'surveying_survey.reconcile',
  SURVEYING_RECONCILIATION_VIEW: 'surveying_reconciliation.view',
  SURVEYING_RECONCILIATION_REVIEW: 'surveying_reconciliation.review',
  SURVEYING_COORDINATE_SYSTEM_CREATE: 'surveying_coordinate_system.create',
  SURVEYING_COORDINATE_SYSTEM_VIEW: 'surveying_coordinate_system.view',
  WORKFORCE_CODE_LIST_VIEW: 'workforce_code_list.view',
  WORKER_ROLE_DEACTIVATE: 'worker_role.deactivate',
  WORKER_VIEW: 'worker.view',
  WORKER_PAYMENT_HISTORY: 'worker.payment_history',
  WORKER_APPROVE: 'worker.approve',
  SUBCONTRACTOR_VIEW: 'subcontractor.view',
  SUBCONTRACTOR_DEACTIVATE: 'subcontractor.deactivate',
  SUBCONTRACTOR_ACTIVATE: 'subcontractor.activate',
  WORKFORCE_SHIFT_VIEW: 'workforce_shift.view',
} as const;

export type PermissionName = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Flat list of every known permission name. Kept in lock-step with the backend
 * `EUserPermissionType` enum by `backend-service/scripts/check-permission-parity.ts`
 * (`yarn check:permission-parity`). When adding a permission, update both sides.
 */
export const ALL_PERMISSION_NAMES: readonly string[] = Object.values(PERMISSIONS);

/** Fuel issues and tank dips: the backend accepts either permission to view them. */
export const FUEL_VIEW_PERMISSIONS: string[] = [
  PERMISSIONS.SHIFTREPORT_VIEW,
  PERMISSIONS.INVENTORY_VIEW,
];

/** Wildcard returned by the backend for super admins — grants everything. */
export const ALL_PERMISSIONS = '*';

/**
 * True if `held` satisfies `required`. Super admins (`*`) pass everything.
 * Also supports resource-level wildcards in the held set (e.g. `user.*`).
 */
export function hasPermission(
  held: readonly string[] | undefined,
  required: string,
): boolean {
  if (!held || held.length === 0) return false;
  if (held.includes(ALL_PERMISSIONS)) return true;
  if (held.includes(required)) return true;
  const [resource] = required.split('.');
  return held.includes(`${resource}.*`);
}

/** True if `held` satisfies AT LEAST ONE of `required`. */
export function hasAnyPermission(
  held: readonly string[] | undefined,
  required: readonly string[],
): boolean {
  if (required.length === 0) return true;
  return required.some((p) => hasPermission(held, p));
}

/** True if `held` satisfies EVERY one of `required`. */
export function hasAllPermissions(
  held: readonly string[] | undefined,
  required: readonly string[],
): boolean {
  return required.every((p) => hasPermission(held, p));
}